import { ColumnsType } from "antd/es/table";
import { ILeTimeInStationsReport, TableReportVrUser } from "shared/types";
import { formatTimeMS } from "./date-utils";
import { VR_USER_COLUMNS } from "pages/vr-users-page/columns/userReportColumns";

const GLOBAL_WIDTH_LE_COLUMNS=250

export const LE_DISPLAY_NAMES: Record<string, { id: string; name: string }> = {
    CurioOrientationQuickStart: {
        id: "00b",
        name: "Basic Orientation",
    },
    PizzaProbability: {
        id: "01",
        name: "Sample Spaces",
    },
    RightTriangleSimilarityLearningExperience: {
        id: "02",
        name: "Right Triangle Similarities",
    },
    LE018_VolumeOfACone: {
        id: "04",
        name: "Volume of Cones",
    },
    LE031_TriangleCongruence: {
        id: "06",
        name: "SSS Triangles",
    },
    LE32_PizzaStats: {
        id: "09",
        name: "Data Order Analysis",
    },
    LE021_CirclePropertiesInAstronomicalObservatoryDesign: {
        id: "11",
        name: "Circle Properties",
    },
};

export function formatExperienceName(name: string) {
    if (name.includes(' ')) return name;

    return name.replace(/([a-z])([A-Z])/g, '$1 $2');
};

/**
 * Orders LE keys by the backend's learning_experiences.sort_order (the
 * curriculum order set in ETL migration 020), then by key; LEs without a
 * sort order go last.
 */
export const compareLeKeys = (
    leData: Record<string, { sortOrder?: number | null }> | undefined,
) => (a: string, b: string): number => {
    const aOrder = leData?.[a]?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const bOrder = leData?.[b]?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder || a.localeCompare(b, undefined, { sensitivity: 'base' });
};

export const getLeById = (id: string) => {
    return Object.values(LE_DISPLAY_NAMES).find(le => le.id === id) ?? null;
};

export const generateLeTimeInStationsColumns = (
    vrUsers: ILeTimeInStationsReport[]
): ColumnsType<ILeTimeInStationsReport> => {

    const baseColumns: any[] = [
        {
            title: 'First Name',
            key: 'firstname',
            dataIndex: 'firstname',
            width: 100,
            ellipsis: true,
            sortKey: 'firstname',
            sorter: true,
            fixed: 'left' as const,
        },
        {
            title: 'Last Name',
            key: 'lastname',
            dataIndex: 'lastname',
            width: 100,
            ellipsis: true,
            sortKey: 'lastname',
            sorter: true,
            fixed: 'left' as const,
        },
        {
            title: 'Email',
            key: 'email',
            dataIndex: 'email',
            width: 220,
            ellipsis: true,
            sortKey: 'email',
            sorter: true,
        },
        // {
        //     title: 'CurioXR #',
        //     key: 'curionumber',
        //     dataIndex: 'curionumber',
        //     width: 120,
        //     sorter: (a, b) => (a.curionumber ?? '').localeCompare(b.curionumber ?? ''),
        // },
    ];

    const leColumns: any[] = [];

    const allLEs = new Set<string>();
    vrUsers.forEach((user) => {
        Object.keys(user?.leData ?? {}).forEach((le) => allLEs.add(le));
    });

    const uLeData = vrUsers[0]?.leData;
    
    const sortedLEs = Array.from(allLEs).sort(compareLeKeys(uLeData));

    sortedLEs.forEach((le) => {
        const stationsSet = new Set<string>();
        vrUsers.forEach((user) => {
            const leInfo = user?.leData?.[le];
            if (leInfo?.stations) {
                Object.keys(leInfo.stations).forEach((station) => stationsSet.add(station));
            }
        });

        const stations = Array.from(stationsSet).sort((a, b) => Number(a) - Number(b));
        // const formattedLe = getLeById(le)?.name ?? `LE ${le}`;
        const formattedLe = uLeData[le]?.leName ?? `LE ${le}`;

        const colCompleted: any = {
            title: `${formattedLe} Stations Completed`,
            key: `${le}_stations`,
            sortKey: `${le}_stations`,
            width: GLOBAL_WIDTH_LE_COLUMNS,
            render: (user: any) => {
                const leInfo = user?.leData?.[le];
                if (!leInfo?.stations) return '';
                const completedCount = Object.values(leInfo.stations).filter(Boolean).length;
                return `${completedCount} / ${Object.keys(leInfo.stations).length}`;
            },
            sorter: (a: any, b: any) => {
                const valA = Object.values(a?.leData?.[le]?.stations ?? {}).filter(Boolean).length;
                const valB = Object.values(b?.leData?.[le]?.stations ?? {}).filter(Boolean).length;
                return valA - valB;
            },
        };
        leColumns.push(colCompleted);

        const colTotal: any = {
            title: `${formattedLe} Total Time (min:sec)`,
            key: `${le}_totalTime`,
            sortKey: `${le}_totalTime`,
            width: GLOBAL_WIDTH_LE_COLUMNS,
            render: (user: any) => formatTimeMS(user?.leData?.[le]?.totalTimeMs ?? 0),
            sorter: (a: any, b: any) => {
                const valA = a?.leData?.[le]?.totalTimeMs ?? 0;
                const valB = b?.leData?.[le]?.totalTimeMs ?? 0;
                return valA - valB;
            },
        };
        leColumns.push(colTotal);

        stations.forEach((station) => {
            const stationIndex = Number(station);

            const col_station = {
            title: `${formattedLe}: St${stationIndex}`,
            key: `${le}_st${stationIndex}`,
            sortKey: `${le}_st${stationIndex}`,
            width: GLOBAL_WIDTH_LE_COLUMNS,
            render: (user: any) => {
                const val = user?.leData?.[le]?.stations?.[stationIndex];
                return val === undefined ? '' : val ? '✔️' : '❌';
            },
            sorter: (a: any, b: any) => {
                const valA = a?.leData?.[le]?.stations?.[stationIndex] ? 1 : 0;
                const valB = b?.leData?.[le]?.stations?.[stationIndex] ? 1 : 0;
                return valA - valB;
            },
            };
        
            leColumns.push(col_station);
        });
    });

    return [...baseColumns, ...leColumns];
};

export const generateUserReportColumns = (
    vrUsers: TableReportVrUser[]
): ColumnsType<ILeTimeInStationsReport> => {

    const baseColumns: ColumnsType<ILeTimeInStationsReport> = VR_USER_COLUMNS.map(col => ({
        title: col.title,
        key: col.key,
        width: col.width,
        sortKey: col.sortKey,
        sorter: col.sorter,
        ellipsis: col.ellipsis,
        ...(col.fixed ? { fixed: col.fixed } : {}),
        render: (user) => col.render(user as any),
    }));

    const leColumns: ColumnsType<ILeTimeInStationsReport> = [];

    const allLEs = new Set<string>();
    vrUsers.forEach((user) => {
        Object.keys(user?.leData ?? {}).forEach((le) => allLEs.add(le));
    });

    const uLeData = vrUsers[0]?.leData;

    const sortedLEs = Array.from(allLEs).sort(compareLeKeys(uLeData));

    sortedLEs.forEach((le) => {
        const stationsSet = new Set<string>();
        vrUsers.forEach((user) => {
            const leInfo = user?.leData?.[le];
        
            if (leInfo?.stations) {
                Object.keys(leInfo.stations).forEach((station) =>
                    stationsSet.add(station)
                );
            }
            if (leInfo?.stationsTimeDuration) {
                Object.keys(leInfo.stationsTimeDuration).forEach((key, index) =>
                    stationsSet.add(String(index))
                );
            }
        });

        const stations = Array.from(stationsSet).sort((a, b) => Number(a) - Number(b));
        // const formattedLe = getLeById(le)?.name ?? `LE ${le}`;
        const formattedLe = uLeData[le]?.leName ?? `LE ${le}`;
        const col_completed: any = {
            title: `${formattedLe} Stations Completed`,
            key: `${le}_stations`,
            sortKey: `${le}_stations`,
            width: GLOBAL_WIDTH_LE_COLUMNS,
            render: (user: any) => {
                const leInfo = user?.leData?.[le];
                if (!leInfo?.stations) return '';
                const completedCount = Object.values(leInfo.stations).filter(Boolean).length;
                return `${completedCount} / ${Object.keys(leInfo.stations).length}`;
            },
            sorter: (a: any, b: any) => {
                const valA = Object.values(a?.leData?.[le]?.stations ?? {}).filter(Boolean).length;
                const valB = Object.values(b?.leData?.[le]?.stations ?? {}).filter(Boolean).length;
                return valA - valB;
            },
        };
        leColumns.push(col_completed);

        const col_total = {
            title: `${formattedLe} Total Time (min:sec)`,
            key: `${le}_totalTime`,
            sortKey: `${le}_totalTime`,
            width: GLOBAL_WIDTH_LE_COLUMNS,
            render: (user: any) => formatTimeMS(user?.leData?.[le]?.totalTimeMs ?? 0),
            sorter: (a: any, b: any) => {
                const valA = a?.leData?.[le]?.totalTimeMs ?? 0;
                const valB = b?.leData?.[le]?.totalTimeMs ?? 0;
                return valA - valB;
            },
        }

        leColumns.push(col_total);

        stations.forEach((station) => {
            const stationIndex = Number(station);

            const col_station = {
                title: `${formattedLe}: St${stationIndex}`,
                key: `${le}_st${stationIndex}`,
                sortKey: `${le}_st${stationIndex}`,
                width: GLOBAL_WIDTH_LE_COLUMNS,
            
                render: (user: any) => {
                    const leInfo = user?.leData?.[le];
                    const durationMs = leInfo?.stationsTimeDuration?.[stationIndex];
            
                    return formatTimeMS(durationMs ?? 0);
                },
            
                sorter: (a: any, b: any) => {
                    const valA = a?.leData?.[le]?.stationsTimeDuration?.[stationIndex] ?? 0;
                    const valB = b?.leData?.[le]?.stationsTimeDuration?.[stationIndex] ?? 0;
                    return valA - valB;
                },
            };
        
            leColumns.push(col_station);
        });          

        const firstUserLeInfo = vrUsers[0]?.leData?.[le];

        if (firstUserLeInfo?.hasPracticeProblems) {
            const col_extra = {
                title: `${formattedLe} Extra Practice`,
                key: `${le}_extraPractice`,
                sortKey: `${le}_extraPractice`,
                width: GLOBAL_WIDTH_LE_COLUMNS,
                render: (user: any) => {
                    const extra = user?.leData?.[le]?.extraPractice;
                    return (extra?.totalTimeMs ?? 0) > 0 ? formatTimeMS(extra.totalTimeMs) : '0:00';
                },
                sorter: (a: any, b: any) => {
                    const valA = a?.leData?.[le]?.extraPractice?.totalTimeMs ?? 0;
                    const valB = b?.leData?.[le]?.extraPractice?.totalTimeMs ?? 0;
                    return valA - valB;
                },
            };

            leColumns.push(col_extra);
        }
    });

    return [...baseColumns, ...leColumns];
};

type ColumnItem = {
    title: string;
    key: string;
};

type GroupedColumns = {
    base: ColumnItem[];
    le: Record<string, ColumnItem[]>;
};

export const splitColumnsByLE = (columns: ColumnItem[]): GroupedColumns => {
    const base: ColumnItem[] = [];
    const le: Record<string, ColumnItem[]> = {};

    columns.forEach((col) => {
        const key = String(col.key);

        if (!key.includes('_')) {
            base.push(col);
            return;
        }

        const [leName] = key.split('_');

        if (!le[leName]) {
            le[leName] = [];
        }

        le[leName].push(col);
    });

    return { base, le };
};
